import React, { useEffect, useState, Suspense } from 'react';
import { Tabs, Tab, Paper, withStyles, Typography } from '@material-ui/core';
import { BackButton, parseURL } from '../../../sc-utilities';
import CircularProgress from '@material-ui/core/CircularProgress';

import { connect } from 'react-redux';
import * as actions from '../store/billingAccountActions';
import { updateURL } from '../../../sc-utilities';
import { hasPermission } from '../../../sc-utilities';
import Balance from "../../../sc-accountManagement/PrepaidBalance";

const AccountOverview = React.lazy(async () => await import('./AccountOverview'));
const PlanList = React.lazy(async () => await import('./Plans'));
const SoftwareLicenseList = React.lazy(async () => await import('./SoftwareLicense'));
const UsageOverview = React.lazy(async () => await import('./Usage'));

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        margin: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            width: `96.5%`
        }
    },
});

const TabContainer = props => {
    return (
        <Typography component="div" dir={props.dir} style={props.style}>
            {props.children}
        </Typography>
    );
};


const getTabValue = () => {
    let urlArray = window.location.href.split('#');
    return urlArray.length > 1 ? Number(urlArray[1]) : 0;
};

export const getSearchParams = () => {
    let searchParams = parseURL(window.location.search);
    return { ActiveAccountID: searchParams ? searchParams.billingAccountID : null };
};

const BillingAccountsOverview = props => {
    const { billingAccount, permissions, activeCustomerID, translations, onCleanState } = props;
    const {
        classes,
        theme,
        onLoadAccount,
        editBillingAccount,
        onAccountPlansList,
        onListUsers,
        editing
    } = props;
    const [tabValue, setTabValue] = useState(getTabValue());
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await onLoadAccount({ ...getSearchParams(), ActiveCustomerID: activeCustomerID });
            let data = await onListUsers({ ActiveCustomerID: activeCustomerID });
            setUsers(data);
            setLoading(false);
        };

        fetchData();
        return () => {
            onCleanState();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onLoadAccount, onCleanState, onListUsers, activeCustomerID]);

    const handleChange = (event, value) => {
        updateURL(value);
        setTabValue(value);
    };

    const tabParam =  window.location.href.split("#");
    if (tabParam && tabParam[1] && tabValue && tabValue!==Number(tabParam[1])) {
        setTabValue(Number(tabParam[1]));
    }

    return (
        <div>
            <Paper className={classes.root}>
                {BackButton(translations)}
                {
                    billingAccount ?
                        <Tabs
                            value={tabValue}
                            onChange={handleChange}
                            indicatorColor="primary"
                            style={{ padding: 0 }}
                            textColor="primary"
                            variant="scrollable"
                            scrollButtons="on"
                        >
                            {hasPermission(permissions, 'billing_account_load') ? <Tab label={translations["account_info"] || "Account info"} /> : null}
                            {hasPermission(permissions, 'billing_account_plan_list') ? <Tab label={translations["account_plans"] || "Account plans"} /> : null}
                            {hasPermission(permissions, 'balance_list') ? <Tab label={translations['balance'] || "Balance"} /> : null}
                            {hasPermission(permissions, 'software_license_list') ? <Tab label={translations["software_licenses"] || "Software licenses"} /> : null}
                            {hasPermission(permissions, 'action_license_list') ? <Tab label={translations["usage"] || "Usage"} /> : null}
                        </Tabs>
                        : null
                }
            </Paper>
            {
                billingAccount && !loading ?
                    <>
                        {hasPermission(permissions, 'billing_account_load') ? <TabContainer style={{ paddingLeft: 6, paddingRight: 6 }} dir={theme.direction}><Suspense fallback={<div><CircularProgress /></div>}>{tabValue < 1 ? <AccountOverview activeCustomerID={activeCustomerID} permissions={permissions} translations={translations} billingAccount={billingAccount} editBillingAccount={editBillingAccount} users={users} onLoadAccount={onLoadAccount} loading={editing} /> : null}</Suspense></TabContainer> : null}
                        {hasPermission(permissions, 'billing_account_plan_list') ? <TabContainer style={{ paddingLeft: 6, paddingRight: 6 }} dir={theme.direction}><Suspense fallback={<div><CircularProgress /></div>}>{tabValue === 1 ? <PlanList activeCustomerID={activeCustomerID} translations={translations} onAssignPlans={props.onAssignPlans} onLoadAccount={onLoadAccount} onListPlans={props.onListPlans} extraFilterParams={getSearchParams()} permissions={permissions} onAccountPlansList={onAccountPlansList} removePlans={props.removePlans} billingAccount={billingAccount} /> : null}</Suspense></TabContainer> : null}
                        {hasPermission(permissions, 'balance_list') ? <TabContainer style={{ paddingLeft: 6, paddingRight: 6 }} dir={theme.direction}><Suspense fallback={<div><CircularProgress /></div>}>{tabValue === 2 ? <Paper><Balance isTab={true} activeAccountID={getSearchParams().ActiveAccountID} /></Paper> : null}</Suspense></TabContainer> : null}
                        {hasPermission(permissions, 'software_license_list') ? <TabContainer style={{ paddingLeft: 6, paddingRight: 6 }} dir={theme.direction}><Suspense fallback={<div><CircularProgress /></div>}>{tabValue === 3 ? <SoftwareLicenseList activeCustomerID={activeCustomerID} extraFilterParams={getSearchParams()} translations={translations} permissions={permissions} /> : null}</Suspense></TabContainer> : null}
                        {hasPermission(permissions, 'action_license_list') ? <TabContainer style={{ paddingLeft: 6, paddingRight: 6 }} dir={theme.direction}><Suspense fallback={<div><CircularProgress /></div>}>{tabValue === 4 ? <UsageOverview translations={translations} extraFilterParams={getSearchParams()} activeCustomerID={activeCustomerID} permissions={permissions} /> : null}</Suspense></TabContainer> : null}
                    </>
                    : null
            }
        </div>
    );
}

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        billingAccount: state.billing.accounts ? state.billing.accounts.loaded : null,
        editing: state.billing.accounts ? state.billing.accounts.editing : false,
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onLoadAccount: params => dispatch(actions.loadBillingAccount(params)),
        onCleanState: params => dispatch(actions.cleanState(params)),
        editBillingAccount: params => dispatch(actions.editBillingAccount(params)),
        onAccountPlansList: params => dispatch(actions.onAccountPlansList(params)),
        onAssignPlans: billingAccount => dispatch(actions.assignPlans(billingAccount)),
        onListPlans: id => dispatch(actions.listPlans(id)),
        removePlans: params => dispatch(actions.removePlans(params)),
        onListUsers: id => dispatch(actions.listUsers(id)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(BillingAccountsOverview));